import { initializeApp } from "firebase/app";
import { 
  getFirestore,
  connectFirestoreEmulator,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  query,
  where,
  onSnapshot,
  orderBy,
  documentId,
  deleteField,
  limit,
  startAfter } from "firebase/firestore";
import i18n from '@/plugins/i18n';
import { mapActions, mapState } from "vuex";
import { tools } from '@/mixins/tools';
import {
  getAuth, signOut, onAuthStateChanged,
  updateProfile,
  connectAuthEmulator
} from "firebase/auth";
import { getStorage, ref, connectStorageEmulator, listAll, getDownloadURL } from "firebase/storage";
import { getApp } from "firebase/app";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

import store from '@/store';
import VIEWS from "../config/views.json";

const LAST_PROCESSES_SIZE = 5;

// Index 0 is the cursor of page 1: the last document of each visited page.
let processPageCursors = [];

// Lets a client-side search notice that a newer one superseded it.
let currentProcessSearchToken = 0;

// Processes exist in two shapes: the current one writes inputsInfo/outputsInfo
// with fileName and descriptorName, older ones a root inputFileName and
// inputDescriptorInfo/outputDescriptorInfo with value. Both are searched.
const asArray = (value) => (Array.isArray(value) ? value : [value].filter(Boolean));

const PROCESS_TEXT_SEARCH_FIELDS = {
  inputFileName: (process) => [
    process.inputFileName,
    ...asArray(process.inputsInfo).map((input) => input?.fileName),
  ],
  inputDescriptorInfo: (process) => [
    ...asArray(process.inputDescriptorInfo).map((input) => input?.value),
    ...asArray(process.inputsInfo).map((input) => input?.descriptorName),
  ],
  outputDescriptorInfo: (process) => [
    ...asArray(process.outputDescriptorInfo).map((output) => output?.value),
    ...asArray(process.outputsInfo).map((output) => output?.descriptorName),
  ],
};

// Firestore rejects an "in" filter with more values than this.
const MAX_IN_FILTER_VALUES = 30;

// Documents per round trip when filtering on the client; batches are sequential.
const PROCESS_SEARCH_BATCH_SIZE = 500;

// loadingPage is a counter: every increment needs exactly one decrement, even
// when the load is superseded before it finishes.
let releaseProcessLoading = null;

const startProcessLoading = () => {
  if (releaseProcessLoading) releaseProcessLoading();

  store.dispatch("setLoadingPage", true);
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    if (releaseProcessLoading === release) releaseProcessLoading = null;
    store.dispatch("setLoadingPage", false);
  };

  releaseProcessLoading = release;
  return release;
};

// Firestore reports a missing index here, with the url to create it.
const reportProcessQueryError = (error) => {
  console.error("[process history] query failed", error);
};

// Past the "in" limit the user filter falls back to the client.
const isServerUserFilter = (filters) =>
  !!filters.user?.length && [].concat(filters.user).length <= MAX_IN_FILTER_VALUES;

const matchesProcessClientSearch = (process, filters) => {
  const matchesEveryTextField = Object.entries(PROCESS_TEXT_SEARCH_FIELDS).every(([field, getValues]) => {
    const searchedText = filters[field];
    if (!searchedText) return true;

    return getValues(process).some(
      (value) => typeof value === "string" && value.toLowerCase().includes(searchedText.toLowerCase())
    );
  });

  if (!matchesEveryTextField) return false;
  if (!filters.user?.length || isServerUserFilter(filters)) return true;

  return [].concat(filters.user).includes(process.user?.email);
};

const needsProcessClientSearch = (filters) =>
  Object.keys(PROCESS_TEXT_SEARCH_FIELDS).some((field) => !!filters?.[field]) ||
  (!!filters?.user?.length && !isServerUserFilter(filters));

const buildProcessSearchConstraints = (filters) => {
  const constraints = [];

  if (filters.status?.length) constraints.push(where("status", "in", [].concat(filters.status)));
  if (filters.processType?.length) constraints.push(where("processType", "in", [].concat(filters.processType)));
  // By email: the name comes from the auth displayName and may be empty.
  if (isServerUserFilter(filters)) constraints.push(where("user.email", "in", [].concat(filters.user)));

  if (filters.startDate) {
    const [year, month, day] = filters.startDate.split("-").map(Number);
    if ([year, month, day].every(Number.isInteger)) {
      constraints.push(
        where("startDate", ">=", new Date(year, month - 1, day)),
        where("startDate", "<", new Date(year, month - 1, day + 1))
      );
    }
  }

  return constraints;
};

const toProcess = (document) => {
  const processFields = document.data();
  return {
    ...processFields,
    id: document.id,
    percentage: Math.round(
      (processFields.numberReadPackages * 100) / processFields.totalNumberPackages
    ),
  };
};

const firebaseConfig = {
  apiKey: window.PROJECT_ENV.API_KEY,
  authDomain: window.PROJECT_ENV.AUTH_DOMAIN,
  projectId: window.PROJECT_ENV.PROJECT_ID,
  storageBucket: window.PROJECT_ENV.STORAGE_BUCKET,
  messagingSenderId: window.PROJECT_ENV.GOOGLE_APP_ID,
  appId: window.PROJECT_ENV.APP_ID,
};
const app = initializeApp(firebaseConfig);
const storage = getStorage();
const db = getFirestore(app);
const auth = getAuth();
const functions = getFunctions(getApp());
let expirationTimeOut = undefined;
if (location.hostname === window.PROJECT_ENV.LOCAL_HOSTNAME) {
  // Point to the Storage emulator running on localhost.
  connectAuthEmulator(auth, `http://localhost:9099`);
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

export const firebaseTools = {
  mixins: [tools],
  computed: {
    ...mapState(["actualView"]),
  },
  methods: {
    ...mapActions(["setShowSnackBar", "setUserLogged"]),
    subscribeOnSnapshot(collectionName, queryActualView, saveData = true) {
      const viewName = tools.methods.collectionStore(collectionName);
      if (!store.state[viewName] || !store.state.unsubscribe[viewName]) {
        let list = [];
        const queryOnSnapshot = queryActualView
          ? query(
              collection(db, collectionName),
              where(
                queryActualView.atribute,
                queryActualView.operator,
                queryActualView.value
              ),
              where("userGroup", "==", store.state.actualGroup.id),
              orderBy(queryActualView.orderBy || "name", "asc")
            )
          : query(
              collection(db, collectionName),
              where("userGroup", "==", store.state.actualGroup.id),
              orderBy("name", "asc")
            );

          store.dispatch("setLoadingPage", true);
          const unsubscribe = onSnapshot(queryOnSnapshot, (querySnapshot) => {
            list = [];
            querySnapshot.forEach((doc) => {
              list.push({ ...doc.data(), id: doc.id });
            });
            if (saveData) {
              if (store.state.itemToSelect) {
                let newItem = list.find(
                  (element) => element.name === store.state.itemToSelect.name
                );
                store.dispatch("setItemToSelect", newItem);
              }
              const actualView = store.state.actualView.parent || store.state.actualView;
              store.dispatch(
                tools.methods.collectionStoreDispatcher(collectionName),
                list
              );

              const { meta, get = [], parent } = actualView;
              const isValidView = !meta?.parent && !parent && get[0]?.collection;
              const isMatchingView = tools.methods.collectionStore(get[0]?.collection) === viewName;

              if (isValidView && isMatchingView) {
                store.dispatch("setDataView", list);
              }
            } else store.dispatch("setVolatileData", list);
            store.dispatch("setLoadingPage", false);
          });
          store.dispatch("setUnsubscribe", { [viewName]: unsubscribe });
      }
    },
    subscribeOnSnapshotById(collectionName, queryActualView = null, documentId ) {
      this.unsubscribeElement("Detail");
      this.unsubscribeElement("DetailDictionary");

      if (!queryActualView && typeof documentId === "string") {
        const parentName = store.state.actualView?.parent?.name || store.state.actualView?.meta?.parent || undefined;

        const queryOnSnapshot = query(doc(db, collectionName, documentId));
          store.dispatch("setLoadingPage", true);

          const unsubscribe = onSnapshot(queryOnSnapshot, { includeMetadataChanges: true }, (querySnapshot) => {
              const { userGroup } = querySnapshot.data() || {};
  
              const isUserInCorrectGroup = userGroup === store.state.actualGroup.id
              const isValidOutputDefinition = parentName === "OutputDefinitionsDetail" && collectionName === "outputDescriptors"
              const isValidInputDefinition = parentName === "InputDefinitionsDetail" && collectionName === "inputDescriptors"
              const isValidDictionariesDefinition = parentName === "Dictionaries"
              const isValidDefinition = isValidOutputDefinition || isValidInputDefinition || isValidDictionariesDefinition
              if (isUserInCorrectGroup && isValidDefinition) {
                store.dispatch("setDataView", querySnapshot.data());
              } else if (store.state.isDeletedDocument && isValidDefinition) {
                store.dispatch("setDataView", querySnapshot.data());
                
                store.dispatch("setIsDeletedDocument", false);
              }
              else {
                store.dispatch("setDataView", undefined);
                store.dispatch("setRouteVuex", "Error404");
              }

              store.dispatch("setLoadingPage", false);
            }
          );
          store.dispatch("setUnsubscribe", {
            Detail: unsubscribe,
            DetailDictionary: unsubscribe,
          });
      }
    },
    subscribeOnSnapshotUser(userId) {
      const queryOnSnapshot = query(doc(db, "users", userId));
      const unsubscribe = onSnapshot(queryOnSnapshot, (querySnapshot) => {
        const newUserLogged = {
          ...store.state.userLogged,
          ...querySnapshot.data(),
        };

        store.dispatch("setUserLogged", newUserLogged);
        i18n.locale = newUserLogged.lang;
        this.$vuetify.lang.current = newUserLogged.lang;
        this.getUserGroups();
      });
      store.dispatch("setUnsubscribe", { userLogged: unsubscribe });
    },
    getUserGroups() {
      let userGroups = [];
      const queryGroups = query(
        collection(db, "groups"),
        where(documentId(), "in", store.state.userLogged.groups)
      );
      getDocs(queryGroups)
        .then((querySnapshot) => {
          querySnapshot.forEach((document) => {
            userGroups.push({ id: document.id, name: document.data().name });
          });
          store.dispatch("setUserGroups", userGroups);
          if (
            !localStorage.getItem("actualGroup") ||
            (localStorage.getItem("actualGroup") &&
              !userGroups.find(
                (group) =>
                  group.id ===
                  JSON.parse(localStorage.getItem("actualGroup")).id
              ))
          ) {
            localStorage.setItem("actualGroup", JSON.stringify(userGroups[0]));
            store.dispatch("setActualGroup", userGroups[0]);
          } else if (localStorage.getItem("actualGroup")) {
            store.dispatch(
              "setActualGroup",
              JSON.parse(localStorage.getItem("actualGroup"))
            );
          }
        })
        .catch((error) => {
          this.handleFireBaseRequests(error.code);
        });
    },
    subscribeOnSnapshotLastProcesses() {
      const lastProcessesQuery = query(
        collection(db, "process"),
        where("user.group", "==", store.state.actualGroup.id),
        orderBy("startDate", "desc"),
        limit(LAST_PROCESSES_SIZE)
      );

      const unsubscribe = onSnapshot(lastProcessesQuery, (querySnapshot) => {
        store.dispatch(
          "setLastProcesses",
          querySnapshot.docs.map((document) => ({
            ...document.data(),
            id: document.id,
          }))
        );
      });

      store.dispatch("setUnsubscribe", { LastProcesses: unsubscribe });
    },
    // Three modes: a single process by id, a page filtered on the client, or
    // the live paginated listener.
    subscribeOnSnapshotProcess() {
      // Layout subscribes again once the group is resolved.
      if (!store.state.actualGroup) return;

      const filters = store.state.processSearchFilters || {};

      currentProcessSearchToken += 1;

      if (filters.id) return this.fetchProcessById(filters.id);

      const searchConstraints = [
        where("user.group", "==", store.state.actualGroup.id),
        ...buildProcessSearchConstraints(filters),
        orderBy("startDate", "desc"),
      ];

      if (needsProcessClientSearch(filters)) return this.fetchProcessPageByClientFilter(searchConstraints, filters);

      return this.subscribeToProcessPage(searchConstraints);
    },
    // The extra document tells whether there is a next page without counting.
    subscribeToProcessPage(searchConstraints) {
      const { processesPerPage, currentProcessPage } = store.state;

      const pageCursor = currentProcessPage > 1 ? processPageCursors[currentProcessPage - 2] : undefined;
      if (currentProcessPage > 1 && !pageCursor) {
        store.dispatch("setCurrentProcessPage", 1);
        return;
      }

      const queryConstraints = [...searchConstraints];
      if (pageCursor) queryConstraints.push(startAfter(pageCursor));
      queryConstraints.push(limit(processesPerPage + 1));

      const knownStatusByProcess = new Map();

      const notifyStatusTransitions = (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === "removed") {
            knownStatusByProcess.delete(change.doc.id);
            return;
          }

          const { status } = change.doc.data();
          const previousStatus = knownStatusByProcess.get(change.doc.id);
          knownStatusByProcess.set(change.doc.id, status);

          // On the first snapshot every document arrives as "added".
          if (change.type !== "modified" || status === previousStatus) return;

          if (status === "FINISHED") this.handleFireBaseRequests("finish-process", null, null);
          else if (status === "FAIL") this.handleFireBaseRequests("error-process", null, null);
        });
      };

      const stopLoadingOnFirstSnapshot = startProcessLoading();

      const unsubscribe = onSnapshot(
        query(collection(db, "process"), ...queryConstraints),
        (querySnapshot) => {
          const hasNextPage = querySnapshot.docs.length > processesPerPage;
          const pageDocs = hasNextPage
            ? querySnapshot.docs.slice(0, processesPerPage)
            : querySnapshot.docs;

          if (!pageDocs.length && currentProcessPage > 1) {
            stopLoadingOnFirstSnapshot();
            store.dispatch("setCurrentProcessPage", currentProcessPage - 1);
            return;
          }

          notifyStatusTransitions(querySnapshot);

          processPageCursors[currentProcessPage - 1] = pageDocs[pageDocs.length - 1];
          store.dispatch("setHasNextProcessPage", hasNextPage);
          store.dispatch("setProcessData", pageDocs.map(toProcess));
          stopLoadingOnFirstSnapshot();
        },
        (error) => {
          stopLoadingOnFirstSnapshot();
          reportProcessQueryError(error);
          this.handleFireBaseRequests(error.code);
        }
      );

      store.dispatch("setUnsubscribe", { ProcessHistory: unsubscribe });
    },
    fetchGroupUsers() {
      const groupUsersQuery = query(
        collection(db, "users"),
        where("groups", "array-contains", store.state.actualGroup.id)
      );

      return getDocs(groupUsersQuery)
        .then((querySnapshot) => {
          const groupUsers = querySnapshot.docs
            .map((document) => {
              const { email, name, displayName } = document.data();
              const userName = name || displayName;
              return email && { value: email, text: userName ? `${userName} (${email})` : email };
            })
            .filter(Boolean)
            .sort((userA, userB) => userA.text.localeCompare(userB.text));

          store.dispatch("setGroupUsers", groupUsers);
        })
        .catch((error) => {
          reportProcessQueryError(error);
          store.dispatch("setGroupUsers", []);
        });
    },
    async fetchProcessById(processId) {
      this.unsubscribeElement("ProcessHistory");
      const stopLoading = startProcessLoading();
      store.dispatch("setHasNextProcessPage", false);

      try {
        const document = await getDoc(doc(db, "process", processId));
        const process = document.exists() ? toProcess(document) : undefined;
        // A process of another group must not surface through a crafted url.
        const belongsToActualGroup = process?.user?.group === store.state.actualGroup.id;

        store.dispatch("setProcessData", belongsToActualGroup ? [process] : []);
      } catch (error) {
        store.dispatch("setProcessData", []);
        reportProcessQueryError(error);
        this.handleFireBaseRequests(error.code);
      } finally {
        stopLoading();
      }
    },
    // Firestore has no substring operator: documents are walked in batches
    // until the page is filled.
    async fetchProcessPageByClientFilter(searchConstraints, filters) {
      const { processesPerPage, currentProcessPage } = store.state;
      const searchToken = currentProcessSearchToken;

      const pageCursor = currentProcessPage > 1 ? processPageCursors[currentProcessPage - 2] : undefined;
      if (currentProcessPage > 1 && !pageCursor) {
        store.dispatch("setCurrentProcessPage", 1);
        return;
      }

      this.unsubscribeElement("ProcessHistory");
      const stopLoading = startProcessLoading();

      const matchedDocuments = [];
      let batchCursor = pageCursor;
      let hasMoreDocuments = true;

      try {
        while (matchedDocuments.length <= processesPerPage && hasMoreDocuments) {
          const batchConstraints = [...searchConstraints];
          if (batchCursor) batchConstraints.push(startAfter(batchCursor));
          batchConstraints.push(limit(PROCESS_SEARCH_BATCH_SIZE));

          const batch = await getDocs(query(collection(db, "process"), ...batchConstraints));
          if (searchToken !== currentProcessSearchToken) return;

          hasMoreDocuments = batch.docs.length === PROCESS_SEARCH_BATCH_SIZE;
          batchCursor = batch.docs[batch.docs.length - 1];

          for (const document of batch.docs) {
            if (matchesProcessClientSearch(toProcess(document), filters)) matchedDocuments.push(document);
            if (matchedDocuments.length > processesPerPage) break;
          }

          // Give the interface a chance to paint between batches.
          await new Promise((resolve) => setTimeout(resolve, 0));
          if (searchToken !== currentProcessSearchToken) return;
        }
      } catch (error) {
        store.dispatch("setProcessData", []);
        reportProcessQueryError(error);
        this.handleFireBaseRequests(error.code);
        return;
      } finally {
        stopLoading();
      }

      const hasNextPage = matchedDocuments.length > processesPerPage;
      const pageDocs = hasNextPage ? matchedDocuments.slice(0, processesPerPage) : matchedDocuments;

      if (!pageDocs.length && currentProcessPage > 1) {
        store.dispatch("setCurrentProcessPage", currentProcessPage - 1);
        return;
      }

      processPageCursors[currentProcessPage - 1] = pageDocs[pageDocs.length - 1];
      store.dispatch("setHasNextProcessPage", hasNextPage);
      store.dispatch("setProcessData", pageDocs.map(toProcess));
    },
    // New filters invalidate every cursor taken from the previous query.
    applyProcessSearch(filters) {
      store.dispatch("setProcessSearchFilters", filters);
      processPageCursors = [];
      store.dispatch("setCurrentProcessPage", 1);

      this.subscribeOnSnapshotProcess();
    },
    resetProcessPagination() {
      processPageCursors = [];
      store.dispatch("setProcessSearchFilters", undefined);
      store.dispatch("setHasNextProcessPage", false);
      store.dispatch("setCurrentProcessPage", 1);
    },
    getAllData(collectionName, queryActualView = null) {
      let list = [];
      const queryOnSnapshot = queryActualView
        ? query(
            collection(db, collectionName),
            where(
              queryActualView.atribute,
              queryActualView.operator,
              queryActualView.value
            ),
            where("userGroup", "==", store.state.actualGroup.id)
          )
        : query(
            collection(db, collectionName),
            where("userGroup", "==", store.state.actualGroup.id)
          );
      return getDocs(queryOnSnapshot)
        .then((querySnapshot) => {
          list = [];
          querySnapshot.forEach((doc) => {
            list.push({ ...doc.data(), id: doc.id });
          });
          return list;
        })
        .catch((error) => {
          this.handleFireBaseRequests(error.code);
        });
    },
    getDataByDocumentId(collectionName, documentId) {
      return getDoc(doc(db, collectionName, documentId))
        .then((querySnapshot) => {
          return querySnapshot;
        })
        .catch((error) => {
          this.handleFireBaseRequests(error.code);
        });
    },
    insertDocument(collectionName, object, nullableFields = []) {
      store.dispatch("setItemToAddEdit", undefined);
      store.dispatch("setItemToAddEditStepper", undefined);
      function deleteNulls(obj) {
        Object.keys(obj).forEach((el) => {
          const objEl = obj[el];
          const deleteable = Boolean(
            (!nullableFields.includes(el) &&
              ([null, undefined, ""].includes(objEl) ||
                (Array.isArray(objEl) && !objEl.length))) ||
              (typeof objEl === "object" && !Object.keys.length)
          );

          if (typeof objEl === "object" && !Array.isArray(obj) && !!objEl)
            deleteNulls(objEl);
          else if (deleteable) delete obj[el];
        });
      }
      deleteNulls(object);

      return addDoc(collection(db, collectionName), object)
        .then((querySnapshot) => {
          collectionName === "process"
            ? this.handleFireBaseRequests("success-process", object.name, "add")
            : this.handleFireBaseRequests("success-CRUD", object.name, "add");
          return querySnapshot;
        })
        .catch(() => {
          this.handleFireBaseRequests("error-CRUD", object.name, "add");
        });
    },
    updateDocument(
      collectionName,
      documentId,
      object = {},
      actionSetted = undefined,
      nameNotification = null,
      deleteNullValuesParam = false
    ) {
      store.dispatch("setLoadingData", true);
      if (!nameNotification) nameNotification = object?.nameNotification;
      delete object?.nameNotification;
      let values = object;

      let itemToAddEdit =
        store.state.itemToAddEdit ||
        store.state.itemToAddEditStepper ||
        store.state.itemToConfirmAction;
      const {
        params,
        deleteNullValues = deleteNullValuesParam,
        nullableFields = [],
        isLastItemToDelete = false,
      } = itemToAddEdit?.data || {};
      const {
        msgAction = undefined,
        updateValuesFB = undefined,
        nameToNotify = undefined,
      } = params || {};
      let action = actionSetted || msgAction || "edit";

      const {
        operator = null,
        atribute = null,
        rawUpdate,
        parentAttr,
      } = updateValuesFB || {};

      function deleteNulls(obj) {
        Object.keys(obj).forEach((el) => {
          const objEl = obj[el];
          const deleteable = Boolean(
            (!nullableFields.includes(el) &&
              ([null, undefined, ""].includes(objEl) ||
                (Array.isArray(objEl) && !objEl.length))) ||
              (typeof objEl === "object" && !Object.keys.length)
          );

          if (typeof objEl === "object" && !Array.isArray(obj) && !!objEl)
            deleteNulls(objEl);
          if (deleteable && updateValuesFB) delete obj[el];
          else if (deleteable && !updateValuesFB) obj[el] = deleteField();
        });
      }

      if (deleteNullValues) deleteNulls(updateValuesFB ? object : values);
      if (updateValuesFB) {
        if (deleteNullValues) deleteNulls(object);
        if (operator === "deleteField" || isLastItemToDelete) {
          values = { [parentAttr || atribute]: deleteField() };
        } else if (operator === "arrayUnion" || operator === "both") {
          values = { [atribute]: arrayUnion(object) };
        } else if (operator === "arrayRemove" && !rawUpdate) {
          values = { [atribute]: arrayRemove(object) };
        } else if (["nestedArrayAdd", "nestedArrayEdit"].includes(operator)) {
          let dataToSet = { [parentAttr || atribute]: object };
          values = { ...dataToSet };
        }
      }

      store.dispatch("setItemToConfirmAction", undefined);
      store.dispatch("setItemToAddEdit", undefined);
      store.dispatch("setItemToAddEditStepper", undefined);

      const documentRef = doc(db, collectionName, documentId);
      const objectToReturn =
        nameNotification || nameToNotify || object.name || object.alias;
      const code =
        collectionName === "process" ? "success-process" : "success-CRUD";
      
      const forceCleanOldItem = !tools.methods.checkAreEqualObjects(itemToAddEdit?.data?.params?.oldItem, object);
      
      return updateDoc(documentRef, values)
        .then((querySnapshot) => {
          if (operator === "both" && forceCleanOldItem) {
            return updateDoc(documentRef, {
              [atribute]: arrayRemove(itemToAddEdit.data.params.oldItem),
            })
              .then((querySnapshot) => {
                this.handleFireBaseRequests(code, objectToReturn, action);
                return querySnapshot;
              })
              .catch(() => {
                this.handleFireBaseRequests(
                  "error-CRUD",
                  objectToReturn,
                  action
                );
              });
          } else {
            this.handleFireBaseRequests(code, objectToReturn, action);
            return querySnapshot;
          }
        })
        .catch(() => {
          this.handleFireBaseRequests("error-CRUD", objectToReturn, action);
        })
        .finally(() => {
          store.dispatch("setLoadingData", false);
        });
    },
    deleteDocument(collectionName, documentId, name) {
      const actualView = store.state.actualView.parent || store.state.actualView;
      store.dispatch("setItemToConfirmAction", undefined);

      store.dispatch("setIsDeletedDocument", true);
      
      return deleteDoc(doc(db, collectionName, documentId))
        .then((querySnapshot) => {
          this.handleFireBaseRequests("success-CRUD", name, "delete");
          
          if (actualView.meta && actualView.meta.parent) {
            store.dispatch("setRouteVuex", actualView.meta.parent);
          }
          
          return querySnapshot;
        })
        .catch(() => {
          this.handleFireBaseRequests("error-CRUD", name, "delete");
        });
    },
    unsubscribeElement(element) {
      if (store.state.unsubscribe[element]) {
        // setUnsubscribe already releases the previous listener.
        store.dispatch("setUnsubscribe", { [element]: undefined });
      }
    },
    createStorageReference(name) {
      return ref(storage, name);
    },
    async getFilesFromStorage(path) {
      const storageRef = this.createStorageReference(path);
      return listAll(storageRef)
      .then((result) => {
        return (result.items || [])
          .map((item) => ({
            name: item.name,
            size: 0,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      })
      .catch((error) => {
        this.handleFireBaseRequests(error.code);
        return [];
      });
    },
    logOut() {
      signOut(auth)
        .then(() => {
          Object.keys(store.state.unsubscribe).forEach((element) => {
            this.unsubscribeElement(element);
          });
          store.dispatch("setRouteVuex", "Logout");
        })
        .catch((error) => {
          this.handleFireBaseRequests(error.code);
        });
    },
    handleInvalidJWT() {
      store.dispatch("setUserLogged", undefined);
      localStorage.removeItem("userLogged");
      localStorage.removeItem("token");

      signOut(auth)
        .then(() => {
          store.dispatch("setRouteVuex", "Login");
          tools.methods.executeAction({
            action: "cancel",
            confirm: true,
          });
        })
        .catch((error) => {
          this.handleFireBaseRequests(error.code);
        });
    },
    clearTimeOutCheckToken() {
      clearTimeout(expirationTimeOut);
    },
    setTimeoutExpireToken(time) {
      expirationTimeOut = setTimeout(()=> {
        this.expireSession();
      }, time)
    },
    expireSession() {
      let statusConfig = {
        color: 'error',
        icon: 'mdi-alert-circle',
        msg: i18n.t('expiredSession')
      };
      store.dispatch('setHttpStatus', statusConfig);
      this.logOut();
    },
    async checkIfSetStateValidatingToken(value, forceRefreshToken = false) {
      if (!value) {
        return true;
      }
      const isValidToken = await this.checkIfTokenIsValid(true, forceRefreshToken);
      if (!isValidToken) {
        this.expireSession();
        return false; 
      }
      return true;
    },
    async checkIfTokenExists() {
      const accessTokenVuex = store.state.userLogged?.accessToken;
      const accessTokenAuthCurrentUser = auth.currentUser?.stsTokenManager.accessToken;
      const localStorageToken = localStorage.getItem('token');
      return !!(accessTokenVuex || accessTokenAuthCurrentUser || localStorageToken);
    },
    async checkSession() {
      const existToken = await this.checkIfTokenExists();
      const isValidToken = await this.checkIfTokenIsValid();
      if (existToken && !isValidToken) this.expireSession();
    },
    compareTokensAuthAndVuex() {
      const accessTokenVuex = store.state.userLogged?.accessToken;
      const accessTokenAuthCurrentUser = auth.currentUser?.stsTokenManager.accessToken;
      if (auth.currentUser && (accessTokenVuex !== accessTokenAuthCurrentUser)) {
        const updatedUserLogged = {
          ...store.state.userLogged,
          ...auth.currentUser,
        }

        store.dispatch("setUserLogged", updatedUserLogged);
        localStorage.setItem('token', accessTokenAuthCurrentUser);
      }
    },
    async checkIfTokenIsValid(refreshToken = false, forceRefreshToken = false) {
      this.compareTokensAuthAndVuex();
      const expirationTime = store.state.userLogged?.stsTokenManager?.expirationTime ?? 0;
      this.clearTimeOutCheckToken(); 
      const currentTime = Date.now(); 
      const timeUntilExpiration = expirationTime - currentTime;
      const thirtyMinutesInMillis = 30 * 60 * 1000;
      if (timeUntilExpiration > 0) {
        if (refreshToken && (forceRefreshToken || (timeUntilExpiration <= thirtyMinutesInMillis))) {
          const expirationTimeRefreshedToken = await this.refreshTokenAndGetExpirationTime();
          const expirationTimeAfterRefresh = expirationTimeRefreshedToken - Date.now();
          this.setTimeoutExpireToken(expirationTimeAfterRefresh);
          return true;
        } else {
          this.setTimeoutExpireToken(timeUntilExpiration);
          return true;
        }
      } else {
        return false;
      }
    },
    async refreshTokenAndGetExpirationTime() {
      try {
        const idTokenResult = await auth.currentUser.getIdTokenResult(true);
        const expirationTimeRefreshToken = idTokenResult.expirationTime;
        const expirationDate = new Date(expirationTimeRefreshToken);
        const expirationInMillis = expirationDate.getTime();

        const updatedUserLogged = {
          ...store.state.userLogged,
          ...auth.currentUser,
        }
        store.dispatch("setUserLogged", updatedUserLogged);

        localStorage.setItem('token', idTokenResult.token);
        return expirationInMillis;
      } catch (error) {
          return 0;
      }
    },
    subscribeIsUserLogged() {
      onAuthStateChanged(auth, (user) => {
        if (!user) {
          tools.methods.clearAllModals();
          this.$router.history.current.name !== "Login" &&
            this.$router.history.current.name !== "Logout" &&
            store.dispatch("setRouteVuex", "Login");
          this.setUserLogged(undefined);
          localStorage.removeItem("userLogged");
          localStorage.removeItem("token");
          localStorage.removeItem("actualGroup");
          store.dispatch("setActualGroup", undefined);
          store.dispatch("setDataView", undefined);
          store.dispatch("setProcessData", undefined);
        } else {
          
          if (this.$router.history.current.name === "Login")
            store.dispatch("setRouteVuex", "Home");
          store.dispatch("setUserLogged", {
            ...store.state.userLogged,
            ...user,
          });
          store.dispatch("setLoadingPage", true);
          this.subscribeOnSnapshotUser(user.uid);
          store.dispatch("setLoadingPage", false);
        }
      });
    },
    suscribeToAllViews() {
      Object.keys(store.state.unsubscribe).forEach((element) => {
        if (
          element !== "userLogged" &&
          element !== "Detail" &&
          element !== "DetailDictionary"
        ) {
          this.unsubscribeElement(element);
        }
      });

      VIEWS.viewsScheme.forEach(({ get, meta, name }) => {
        const viewsToIgnore = ["ProcessHistory", "ProcessExecution", "JoinProcess", "ValidationProcess", "TransformationProcess", "Home"]
        if (!meta?.parent && !viewsToIgnore.includes(name)) {
          get.forEach((item) => {
            firebaseTools.methods[item.get](
              item.collection,
              item.query || null,
              true
            );
          });
        }
      });
    },
    async updateUserPhoto(photo) {
      try {
        await updateProfile(auth.currentUser, {
          photoURL: photo
        })
        return true
      } catch (error) {
        return false
      }
    },
    downloadStorageFile(fileRef, fileName) {
      return getDownloadURL(fileRef)
        .then(async (url) => {
          await this.downloadFile(
            url,
            "get",
            fileName,
            null
          )
        })
        .catch(() => {
          this.setShowSnackBar({
            color: "error",
            msg: i18n.t("errorDownloadFile"),
            icon: "mdi-alert-circle",
          });
        })
    },
    handleFireBaseRequests(code, element = null, action = null) {
      const actualView =
        store.state.actualView?.parent || store.state.actualView;
      switch (code) {
        case "auth/user-not-found":
          return i18n.t("loginUserError");
        case "auth/wrong-password":
          return i18n.t("loginPswdError");
        case "auth/invalid-email":
          return i18n.t("loginEmailError");
        case "permission-denied":
          store.dispatch("setRouteVuex", "NotAllowed");
          break;
        case "error-CRUD":
          store.dispatch("setShowSnackBar", {
            color: "error",
            icon: "mdi-alert-circle",
            msg: i18n.t(`error.${action}.${actualView.name}`, {
              element: element,
            }),
          });
          break;
        case "success-CRUD":
          store.dispatch("setShowSnackBar", {
            color: "success",
            icon: "mdi-check-circle",
            msg: i18n.t(`success.${action}.${actualView.name}`, {
              element: element,
            }),
          });
          break;
        case "success-process":
          if (action === "edit") return;
          if (actualView.name === "ProcessHistory")
            store.dispatch("setShowSnackBar", {
              color: "info",
              icon: "mdi-information",
              msg: i18n.t("runningProcess"),
            });
          else if (actualView.name !== "Home")
            store.dispatch("setShowSnackBar", {
              color: "info",
              icon: "mdi-information",
              msg: i18n.t("runningProcess"),
              link: { to: "ProcessHistory", text: i18n.t("seeProcessStatus") },
            });
          break;
        case "auth/internal-error":
          store.dispatch("setShowSnackBar", {
            color: "error",
            icon: "mdi-alert-circle",
            msg: i18n.t(`error.emailBlocked`),
          });
          break;
        case "finish-process":
          if (actualView.name === "ProcessHistory")
            store.dispatch("setShowSnackBar", {
              color: "success",
              icon: "mdi-check-circle",
              msg: i18n.t("finishProcess"),
            });
          else if (actualView.name !== "Home")
            store.dispatch("setShowSnackBar", {
              color: "success",
              icon: "mdi-check-circle",
              msg: i18n.t("finishProcess"),
              link: { to: "ProcessHistory", text: i18n.t("seeFileProcesses") },
            });
          break;
        case "error-process":
          if (actualView.name === "ProcessHistory")
            store.dispatch("setShowSnackBar", {
              color: "error",
              icon: "mdi-alert-circle",
              msg: i18n.t(`error.add.ProcessHistory`),
            });
          else if (actualView.name !== "Home")
            store.dispatch("setShowSnackBar", {
              color: "error",
              icon: "mdi-alert-circle",
              msg: i18n.t(`error.add.ProcessHistory`),
              link: { to: "ProcessHistory", text: i18n.t("seeFileProcesses") },
            });
          break;
        default:
          store.dispatch("setShowSnackBar", {
            color: "error",
            icon: "mdi-alert-circle",
            msg: i18n.t("unexpectedError"),
          });
          break;
      }
    },
  },
};
